import { useContext, useEffect, useRef, useState } from 'react';
import { getLocalStorage } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { PeopleContext } from '@shared/context/people';
import { useGetEventByNameForPeoplePickerQuery } from '@shared/components/PeoplePicker/usePeoplePickerButtonController.graphql.generated';
import { addError } from '@shared/utils/logger';
import type { DeliveryMethodValue } from '@shared/components/PeoplePicker/PeoplePicker.types';
import { recomputeDeliveryFlags } from '@shared/utils/messaging/deliveryChannel';
import { getPersonName } from '@shared/components/PeopleListItem/PeopleListItem.utils';

/**
 * localStorage key the legacy monorepo's admin guest list writes when the admin hits its Send
 * button — a JSON array of guest keys (`adminTaskGuestsController#saveGuestsToLocalStorage`, called
 * from `openSendMessages`). The legacy admin and joy-web are the SAME ORIGIN (the same premise the
 * create-message picker's postMessage contract already relies on), so the value reaches us directly.
 *
 * Storage rather than a query param because these compose routes are server-rendered: a query string
 * rides in the HTTP request line, and a select-all is thousands of characters of guest keys, which
 * risks a 414 at the proxy — capping the feature exactly where it matters most.
 */
const LEGACY_SELECTED_GUESTS_KEY = 'selectedGuests';

const readLegacySelectedGuestKeys = (): string[] => {
  try {
    const raw = getLocalStorage().getItem(LEGACY_SELECTED_GUESTS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return Array.from(new Set(parsed.filter((value): value is string => typeof value === 'string' && value.trim().length > 0).map(value => value.trim())));
  } catch (error) {
    addError(error, { context: 'useSelectedGuestsFromGuestList#read' });
    return [];
  }
};

/**
 * Drops the handoff without seeding it anywhere.
 *
 * Exported for the destination picker: {@link useSelectedGuestsFromGuestList} consumes the key on
 * mount, so every destination that mounts it expires the handoff simply by being opened. The card
 * templates never mount it — they choose recipients after checkout, past the point this hook runs —
 * so on those the key would otherwise outlive the compose session entirely and seed a later,
 * unrelated composer with a selection the admin made days ago. Sending to a stale list is a worse
 * failure than opening with an empty one, so the picker expires it on the way out instead.
 */
export const clearLegacySelectedGuestKeys = (): void => {
  try {
    getLocalStorage().removeItem(LEGACY_SELECTED_GUESTS_KEY);
  } catch (error) {
    addError(error, { context: 'useSelectedGuestsFromGuestList#clear' });
  }
};

/**
 * Seeds PeopleContext from the legacy guest list's handoff, so opening the composer from its Send
 * button arrives with the checked guests already selected as recipients.
 *
 * ## Why this resolves the guests instead of seeding sparse entries
 *
 * A legacy person key is a `personIdTag`, NOT a person node `id` — legacy has no node id to give us
 * (its GraphQL `Person` exposes `personKey` only). But the legacy guest list is wedding-only, and
 * every wedding send path keys on `id`:
 *
 *   - draft:     `resolveDraftRecipients` → `selectedPeople.map(p => p.id).filter(Boolean)`
 *   - non-draft: `.filter(person => Boolean(person.id && person.email))`
 *
 * Seeding `{ personIdTag }` alone would render correctly — the PeoplePicker matches on `id` OR
 * `personIdTag` — while both send paths silently resolved to ZERO recipients until the picker's
 * hydration filled in `id`. `hasRecipients` is just `selectedPeople.length > 0`, so Send would be
 * enabled and the button would read "N People Selected" during that window. Sending to nobody is
 * not a state worth rendering, so we resolve the keys to real people BEFORE seeding.
 *
 * The query is the one the PeoplePicker already runs with the same variables, so Apollo serves both
 * from one request; it is skipped entirely when there is no handoff to resolve. Seeding complete
 * entries also makes the picker's own hydration pass a no-op.
 *
 * Reachability is deliberately NOT a filter here. A guest with no email or phone is seeded like any
 * other: including an unsendable recipient is harmless, the admin can resolve it in the picker, and
 * dropping one silently would remove a guest they explicitly checked. (Note that the picker's
 * unreachable-warning banner is local to the picker dialog, so it does not fire on this path — the
 * admin never has to open the picker. Surfacing that on the compose page is a separate question.)
 *
 * Keys that match no person in the event ARE dropped — not for reachability, but because they
 * resolve to nobody at all: there is no person to name, render, or act on.
 *
 * ## Delivery flags
 *
 * `id` is necessary but not sufficient. The SMS-capable composers (text blast, the two reminders,
 * contact collection) don't send to `selectedPeople` — they send to
 * `selectedPeople.filter(isEmailRecipient | isSmsRecipient)`, which reads the per-person
 * `hasEmailDeliveryMethodSelected` / `hasTextDeliveryMethodSelected` flags. An entry with a real
 * `id` but no flags is the same "renders fine, sends to nobody" bug in a different key, so every
 * seeded entry gets its flags computed here, from the caller's `deliveryMethod`, by the same
 * `recomputeDeliveryFlags` those composers apply to a hand-made selection.
 *
 * The default is `'both'`, which is what the email-only destinations (post office, eCard, general
 * message) want: they ignore the flags entirely and key on `id`.
 *
 * This is still not a reachability filter — a guest with no phone is seeded with
 * `hasTextDeliveryMethodSelected: false`, exactly as the picker would mark them. They stay
 * selected and visible; they are just not a text recipient.
 *
 * `hasPhone` is part of that computation, not decoration: `recomputeDeliveryFlags` derives phone
 * validity as `hasValidPhone(phoneDisplay) || hasPhone`, so passing `hasPhone: !!phoneE164`
 * reproduces the picker's own rule (`!!phoneE164 || hasValidPhone(phoneDisplay)`) and keeps the
 * BE-canonical `phoneE164` authoritative. Without it, a guest whose `phoneDisplay` libphonenumber
 * rejects — the schema notes it is "not necessarily valid" — but whose `phoneE164` is set would be
 * seeded as `hasTextDeliveryMethodSelected: false` and silently dropped by every SMS send path.
 * Permanently: the picker's enrichment preserves defined flags (`person.flag ?? view.flag`), so a
 * wrong `false` here is never corrected.
 *
 * ## Display fields
 *
 * The seed also carries the name fields the compose surfaces render, for the same reason it carries
 * `id`: they are read directly, not via the picker. `full_name` feeds the mobile preview header
 * (`RecipientsBlock`, which interpolates it into "{name} and N other guests") and the thank-you
 * confirm copy; `personName` names the guest on the contact-collection success screen. Omitting them
 * renders a literal "undefined and N other guests" until the picker's hydration fills them in, on
 * the surfaces where a picker is mounted at all.
 *
 * `avatarInitials` is deliberately NOT seeded: it comes from joykit's `getInitials`, and importing
 * that into a shared hook puts it in every consumer's spec mock surface for one avatar fallback on
 * a post-send screen, which the picker's enrichment fills in anyway.
 *
 * ## Lifecycle
 *
 * The keys are READ during the first render and CONSUMED (cleared from storage) in the mount
 * effect, before the guests resolve — so a stale selection can never resurrect in a later compose
 * session even if this one is abandoned mid-flight. A refresh of the compose page still keeps its
 * recipients — PeopleProvider persists the selection to sessionStorage under its own `storageKey`.
 *
 * If the query fails we seed nothing and the composer opens empty — the admin sees the recipients
 * are missing, rather than a selection that would have sent to nobody.
 *
 * ## The return value, and why the read happens during render
 *
 * Returns whether a handoff was present at mount. Consumers that apply their own DEFAULT
 * pre-selection (RsvpReminderProvider's "hasn't RSVP'd" set) use it to stand down, exactly as they
 * already do for {@link usePersonIdsFromQueryParams}. Seeding here is asynchronous — the keys are
 * resolved against a query first — so an effect-set flag would land too late: with a warm Apollo
 * cache the consumer's own data-loaded effect fires in the same passive-effect flush and would
 * overwrite the seeded recipients before a re-render could propagate the flag. The value therefore
 * has to be correct on the consumer's FIRST render, which means reading storage during render.
 *
 * That read is SSR-safe: `getLocalStorage()` falls back to an in-memory store when `window` is
 * absent, so the server sees no handoff and this returns `false` there. It cannot cause a hydration
 * mismatch, because nothing this flag feeds affects rendered output on the first client render — it
 * only gates work that happens in effects (which never ran on the server), and the seeding itself
 * lands a render later. Callers must keep it that way: gate effects with it, not markup.
 *
 * Must be called inside a <PeopleProvider>.
 *
 * @param deliveryMethod the composer's effective delivery method — pass the same value it feeds
 *   `recomputeDeliveryFlags` (i.e. already collapsed to `'email'` when SMS is off).
 * @param enabled gates the resolution query and the seeding — pass `false` on a composer variant
 *   that does not read recipients from PeopleContext (the cash-fund one reads formik), so it does
 *   not fetch the whole household list to seed a context nothing there looks at. The key is still
 *   CONSUMED either way, exactly as {@link usePersonIdsFromQueryParams} still strips its param when
 *   gated off: a handoff left in storage would resurrect in whatever composer mounted next.
 */
export const useSelectedGuestsFromGuestList = (deliveryMethod: DeliveryMethodValue = 'both', enabled: boolean = true): boolean => {
  const { eventHandle = '' } = useEventInfo();
  const { setSelectedPeople } = useContext(PeopleContext);

  // Guest keys pending resolution, captured during the first render (see the doc block above).
  // Drives `skip`, so flows without a handoff never issue the query — not even for one render.
  const [pendingGuestKeys, setPendingGuestKeys] = useState<ReadonlyArray<string>>(readLegacySelectedGuestKeys);
  // Latched separately: `pendingGuestKeys` empties once seeding is done, but consumers need the
  // answer to "was there a handoff?" for the life of the compose session.
  const [hasHandoff] = useState(() => pendingGuestKeys.length > 0);
  const seededRef = useRef(false);

  useEffect(() => {
    // Consume unconditionally — including the empty/malformed case — so nothing lingers for a
    // later reader. The keys themselves were already captured above.
    clearLegacySelectedGuestKeys();
  }, []);

  const { data } = useGetEventByNameForPeoplePickerQuery({
    // `batchMode` is required by this repo's QueryHookOptions. Passing it explicitly as `undefined`
    // mirrors the PeoplePicker's own call, so both land on identical options and Apollo serves them
    // from one request rather than two.
    batchMode: undefined,
    variables: { eventHandle },
    skip: !enabled || pendingGuestKeys.length === 0 || !eventHandle,
  });

  useEffect(() => {
    if (seededRef.current) return;
    // Deliberately BEFORE the seeded/pending bookkeeping below: `enabled` can start out false and
    // flip once the event type resolves, and the keys captured at the first render must still be
    // there to seed when it does.
    if (!enabled) return;
    if (pendingGuestKeys.length === 0) return;

    const households = data?.eventByName?.guests.households;
    if (!households) return;

    const personByGuestKey = new Map(households.flatMap(household => household.members).map(member => [member.personIdTag, member]));

    const resolved = pendingGuestKeys.map(guestKey => personByGuestKey.get(guestKey)).filter((person): person is NonNullable<typeof person> => Boolean(person));

    seededRef.current = true;
    setPendingGuestKeys([]);

    if (resolved.length === 0) return;

    setSelectedPeople(
      recomputeDeliveryFlags(
        resolved.map(person => ({
          id: person.id,
          personIdTag: person.personIdTag,
          firstName: person.firstName,
          lastName: person.lastName,
          // The picker builds its label the same way, and leaves it empty for an unnamed guest
          // (its "{leader}'s Guest" fallback needs household position + translations it owns).
          full_name: [person.firstName, person.lastName].filter(Boolean).join(' '),
          personName: getPersonName(person),
          email: person.email,
          hasEmail: person.hasEmail,
          phoneDisplay: person.phoneDisplay,
          phoneE164: person.phoneE164,
          hasPhone: !!person.phoneE164,
          avatarName: person.avatarName,
          avatar: person.avatar,
          household: person.household,
        })),
        deliveryMethod,
      ),
    );
    // `deliveryMethod` is read at seed time only — `seededRef` makes a later change a no-op here,
    // and the composers own the recompute when the admin switches channel.
  }, [data, deliveryMethod, enabled, pendingGuestKeys, setSelectedPeople]);

  // `enabled` is ANDed in rather than latched into `hasHandoff`: a caller that gates seeding off must
  // not stand its own default pre-selection down for a handoff that will never be seeded, and a
  // caller whose gate flips true late (event type resolving) gets the flag then, when it is true.
  return enabled && hasHandoff;
};
